import { useSession, useUser } from "@clerk/clerk-react";
import type { UserResource } from "@clerk/types";
import { CaretDownIcon } from "@radix-ui/react-icons";
import { ToggleGroup } from "@radix-ui/react-toggle-group";
import axios from "axios";
import {
  Ban,
  Blocks,
  CloudUpload,
  FileIcon,
  Loader2,
  PaperclipIcon,
  Plus,
  X,
} from "lucide-react";
import { useActiveFeatureFlags, useFeatureFlagEnabled } from "posthog-js/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import AiModelDropdown from "@/components/AI/components/AiModelDropDown";
import DragHoverTargets from "@/components/AI/components/DragHoverTargets";
import {
  useDirectContextUpload,
  useImageUploader,
} from "@/components/AppHeader/FileUpload";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGlobalDragState } from "@/hooks/useDragState";
import { cn } from "@/lib/utils";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import {
  useProjectCreateStore,
  selectDesignSystem,
} from "@/store/zustand/projectCreateStore";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { track } from "@/utils/events/tracker";
import type { TemplateConfig } from "@/utils/templateProjects";
import {
  CustomDesignSystems,
  type DesignSystem,
  getTemplateForInputs,
  isValidConfiguration,
  ProjectRunMode,
  ReactFramework,
  TemplateStack,
} from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useIsBitbucketConnected,
  useIsGithubConnected,
  useIsGitlabConnected,
  useUserId,
} from "@/utils/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import { ConfigBlock } from "./ConfigBlock";
import { ProviderSetup } from "./ProviderSetup";
import { TechItem } from "./TechItem";
import { TechPopover, TechPopoverGroup, TechPopoverItem } from "./TechPopover";
import { TechSubgroup } from "./TechSubgroup";
import { StylesBuilderModal } from "./StylesBuilder/StylesBuilderModal";
import { useStylesBuilderStore } from "@/store/zustand/stylesBuilderStore";
import {
  getSelectionsSummary,
  generateFullPromptContent,
} from "@/utils/stylesBuilderUtils";
import { Sparkles } from "lucide-react";

type FormValues = {
  clerk: {
    publishableKey: string;
    secretKey: string;
  };
  convex: {
    deploymentUrl: string;
    deploymentKey: string;
  };
  polar: {
    accessToken: string;
    organizationId: string;
    webhookSecret: string;
    verifyWebhookFields: string;
  };
  stripe: {
    secretKey: string;
    webhookSecret: string;
    publicKey: string;
    verifyWebhookFields: string;
  };
};

/**
 * Polls the dev server status and uploads files once the server is ready
 */
const uploadPendingFilesWhenReady = async (
  canvasId: string,
  files: File[],
  uploadFilesFn: (
    files: File[],
    options?: {
      forCodebase?: boolean;
      canvasId?: string;
      codebaseDirectory?: string;
    },
  ) => Promise<void>,
  supabaseJWT: string,
): Promise<void> => {
  const maxAttempts = 60; // 5 minutes max wait time
  const delayMs = 5000; // 5 seconds between checks

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/devServer/devServerStatus`,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      const { isServerRunning, devServerConsoleConnected, containerStatus } =
        response.data;

      // Check if dev server is fully ready
      if (
        containerStatus?.running &&
        isServerRunning &&
        devServerConsoleConnected
      ) {
        // Dev server is ready, upload the files
        await uploadFilesFn(files, {
          forCodebase: true,
          canvasId,
          codebaseDirectory: undefined, // Use default directory logic
        });
        return;
      }

      // Wait before next check
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    } catch (error) {
      console.warn(
        `Dev server status check failed (attempt ${attempt + 1}):`,
        error,
      );

      // If it's a 401/403, the canvas might not be ready yet, continue polling
      if (error.response?.status === 401 || error.response?.status === 403) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }

      // For other errors, wait a bit longer before retrying
      await new Promise((resolve) => setTimeout(resolve, delayMs * 2));
    }
  }

  throw new Error("Dev server did not become ready within the timeout period");
};

const NewProject = () => {
  const navigate = useNavigate();

  const selectedTemplateStack = useProjectCreateStore(
    (state) => state.selectedTemplateStack,
  );
  const setSelectedTemplateStack = useProjectCreateStore(
    (state) => state.setSelectedTemplateStack,
  );
  const selectedDesignSystem = useProjectCreateStore(selectDesignSystem);

  const activeFeatureFlags = useActiveFeatureFlags();

  const allowPickAlternateFramework = useFeatureFlagEnabled(
    "pick-alternate-framework",
  );

  const dispatch = useDispatch();

  const { user } = useUser();

  const [searchParams] = useSearchParams();
  const firstTime = searchParams.get("ft");

  const {
    uploadedFiles,
    FilePreviewerComponent,
    FileUploadComponent,
    uploadFiles,
  } = useImageUploader();

  // Custom hook for direct context upload
  const { handleDirectPaste } = useDirectContextUpload(uploadFiles);

  const { session } = useSession();

  const [generateTextInput, setGenerateTextInput] = useState("");

  const setGenerateAttachedFiles = useProjectCreateStore(
    (state) => state.setGenerateAttachedFiles,
  );

  const pendingCodebaseFiles = useProjectCreateStore(
    (state) => state.pendingCodebaseFiles,
  );
  const stylesBuilderInstructions = useProjectCreateStore(
    (state) => state.stylesBuilderInstructions,
  );
  const hasAppliedStyles = Boolean(stylesBuilderInstructions);
  const addPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.addPendingCodebaseFiles,
  );
  const clearPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.clearPendingCodebaseFiles,
  );
  const setPendingCodebaseFiles = useProjectCreateStore(
    (state) => state.setPendingCodebaseFiles,
  );

  const resetConfiguredOptions = useProjectCreateStore(
    (state) => state.resetConfiguredOptions,
  );

  const setTemplateStackConfigured = useProjectCreateStore(
    (state) => state.setTemplateStackConfigured,
  );

  const reactFramework = useProjectCreateStore((state) => state.reactFramework);
  const setReactFramework = useProjectCreateStore(
    (state) => state.setReactFramework,
  );

  const templateStackConfigured = useProjectCreateStore(
    (state) => state.templateStackConfigured,
  );

  const supabaseConnectedProjectId = useSupabaseConnectStore(
    (state) => state.connectedProjectId,
  );

  const [showIntegrationSetup, setShowIntegrationSetup] = useState<
    "configureOnly" | "configureAndGenerate"
  >();

  const [envVariables, setEnvVariables] = useState<FormValues>({
    clerk: {
      publishableKey: "",
      secretKey: "",
    },
    convex: {
      deploymentUrl: "",
      deploymentKey: "",
    },
    polar: {
      accessToken: "",
      organizationId: "",
      webhookSecret: "",
      verifyWebhookFields: "",
    },
    stripe: {
      secretKey: "",
      webhookSecret: "",
      publicKey: "",
      verifyWebhookFields: "",
    },
  });

  const { isDragging, resetDragState } = useGlobalDragState();

  const codeGenerationModel = useAiActionStore(
    (state) => state.codeGenerationModel,
  );

  const useReasoning = useAiContextStore((state) => state.useReasoning);

  const useSearch = useAiContextStore((state) => state.useSearch);

  const chatOnlyMode = useAiContextStore((state) => state.chatOnlyMode);

  // Styles Builder
  const {
    openModal: openStylesBuilder,
    selections: stylesBuilderSelections,
    hasSelections: hasStylesBuilderSelections,
    reset: resetStylesBuilder,
  } = useStylesBuilderStore();

  const stylesBuilderSummary = getSelectionsSummary(stylesBuilderSelections);

  // Handler for when user applies styles builder selections
  const handleStylesBuilderApply = (customAccentHex: string | null) => {
    // Generate full content (text description + HTML examples) for additional instructions
    const { systemPromptText, additionalInstructions } =
      generateFullPromptContent(stylesBuilderSelections, customAccentHex);

    // Combine both into one string - this will be hidden from user but sent to AI
    const fullStylesContent = `${systemPromptText}\n\n${additionalInstructions}`;
    useProjectCreateStore
      .getState()
      .setStylesBuilderInstructions(fullStylesContent);
  };

  // Handler to clear styles builder selections
  const handleClearStyles = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetStylesBuilder();
    useProjectCreateStore.getState().setStylesBuilderInstructions(null);
  };

  useEffect(() => {
    // Track user entering the project creation flow
    track("PROJECT_CREATION_STEP", {
      step: "PROJECT_CREATION_STARTED",
      canvas_id: null,
      source: "ai-new-page",
      first_time: firstTime === "true",
    });

    // Set defaults
    resetConfiguredOptions();
  }, [firstTime, resetConfiguredOptions]);

  const showDesignSystem =
    useFeatureFlagEnabled("custom_design_system") || true; // TODO: remove override

  const [generating, setGenerating] = React.useState(false);
  const [creatingBlankProject, setCreatingBlankProject] = React.useState(false);
  const loading = creatingBlankProject || generating;

  const userId = useUserId();

  const getEnvironmentVariables = (
    reactFramework: ReactFramework,
    selectedTemplateStack: TemplateStack,
    envVariables: FormValues,
  ) => {
    // For non-Supabase stacks, include all relevant environment variables
    if (reactFramework === ReactFramework.NEXT_JS) {
      // For Supabase stacks, only include payment-related environment variables
      // Supabase connection variables are handled by the connection process
      if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
        return {
          POLAR_ORGANIZATION_ID: envVariables?.polar.organizationId,
          POLAR_ACCESS_TOKEN: envVariables?.polar.accessToken,
          POLAR_WEBHOOK_SECRET: envVariables?.polar.webhookSecret,
        };
      }

      if (selectedTemplateStack === TemplateStack.SUPABASE_STRIPE) {
        return {
          STRIPE_SECRET_KEY: envVariables?.stripe?.secretKey,
          STRIPE_WEBHOOK_SECRET: envVariables?.stripe?.webhookSecret,
        };
      }
    }

    if (reactFramework === ReactFramework.VITE) {
      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX) {
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR) {
        // For Vite with Polar, don't include Polar keys as they'll be stored in Convex
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE) {
        // For Vite with Stripe, don't include Stripe keys as they'll be stored in Convex
        return {
          VITE_CLERK_PUBLISHABLE_KEY: envVariables?.clerk?.publishableKey,
          CONVEX_DEPLOYMENT:
            envVariables?.convex?.deploymentKey?.split("|")?.[0],
          VITE_CONVEX_URL: envVariables?.convex?.deploymentUrl,
        };
      }

      // Handle Vite with Supabase and payment providers
      if (selectedTemplateStack === TemplateStack.SUPABASE_STRIPE) {
        return {
          STRIPE_SECRET_KEY: envVariables?.stripe?.secretKey,
          STRIPE_WEBHOOK_SECRET: envVariables?.stripe?.webhookSecret,
        };
      }

      if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
        return {
          POLAR_ORGANIZATION_ID: envVariables?.polar.organizationId,
          POLAR_ACCESS_TOKEN: envVariables?.polar.accessToken,
          POLAR_WEBHOOK_SECRET: envVariables?.polar.webhookSecret,
        };
      }
    }

    return {};
  };

  const createProject = async (
    template: TemplateConfig,
    user: UserResource | null | undefined,
    supabaseJWT: string,
    envVariables?: Record<string, string>,
    designSystem?: DesignSystem | null,
  ) => {
    if (!userId) {
      throw new Error("User not found");
    }

    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`,
      {
        repoName: template.projectName,
        repoPath: template.repoPath,
        framework: template.framework,
        nodeVersion: template.nodeVersion,
        packageManager: template.packageManager,
        userId: userId,
        runMode: ProjectRunMode.CLOUD,
        environmentVariables: envVariables || {},
        designSystem: designSystem,
        language: template.language,
      },
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      },
    );

    useDatabaseSyncWrapperStore.getState().triggerRefetchProjects();
    return response.data;
  };

  const generateBlankProject = async () => {
    try {
      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        return;
      }

      setCreatingBlankProject(true);

      const template = getTemplateForInputs(
        reactFramework,
        TemplateStack.BLANK,
      );

      const { project: createdProject, canvas: createCanvas } =
        await createProject(template, user, supabaseJWT, {}, null);

      track("ADD_PROJECT", { step: "GENERATE", canvas_id: createCanvas.id });

      track("TRACK_USED_RUNNING_TEMPLATE", {
        used: Boolean(createdProject?.templateUserId),
        repoPath: createdProject?.repoPath,
      });

      // Update store
      dispatch(
        onProjectsSnapshot({
          newItems: [createdProject],
          deletedItems: [],
        }),
      );

      // Keep the user's selected model for the new project
      navigate(`/canvases/${createCanvas.id}/editor?open-design-mode=t`);
    } catch (error) {
      logAxiosError("Error creating new project", error);

      // Track project creation failure
      track("PROJECT_CREATION_STEP", {
        step: "PROJECT_CREATION_FAILED",
        canvas_id: null,
        error: getAxiosErorrMessage(error),
        template_stack: selectedTemplateStack,
        framework: reactFramework,
      });

      toast.error(
        `Could not create a new project: ${getAxiosErorrMessage(error)}`,
      );
    } finally {
      setCreatingBlankProject(false);
    }
  };

  const generate = async (templateStackConfiguredOverride = false) => {
    if (!templateStackConfigured && !templateStackConfiguredOverride) {
      setShowIntegrationSetup("configureAndGenerate");
      return;
    }

    if (loading) return;

    if (!generateTextInput || generateTextInput.trim() === "") {
      toast.error("Please enter a prompt to generate your project");
      return;
    }

    // Track user input submission
    track("PROJECT_CREATION_STEP", {
      step: "PROJECT_PROMPT_SUBMITTED",
      canvas_id: null,
      prompt_length: generateTextInput.length,
      has_attachments: uploadedFiles.length > 0,
      pending_codebase_files: pendingCodebaseFiles.length,
      template_stack: selectedTemplateStack,
      framework: reactFramework,
      first_time: firstTime === "true",
    });

    useProjectCreateStore.getState().setGenerateText(generateTextInput);

    try {
      const supabaseJWT = await getUnexpiredToken(session);

      if (!supabaseJWT) {
        return;
      }

      setGenerating(true);

      const template = getTemplateForInputs(
        reactFramework,
        selectedTemplateStack,
      );

      if (!template) {
        toast.error("Could not find a suitable template");
        return;
      }

      // Format environment variables for the selected stack
      // For Supabase stacks, we don't need to include Supabase env vars as they'll be handled by the connection
      const rawEnvVars = getEnvironmentVariables(
        reactFramework,
        selectedTemplateStack,
        envVariables,
      );

      // Filter out undefined values
      const formattedEnvVars = Object.fromEntries(
        Object.entries(rawEnvVars).filter(([_, value]) => value !== undefined),
      ) as Record<string, string>;

      const { project: createdProject, canvas: createCanvas } =
        await createProject(
          template,
          user,
          supabaseJWT,
          formattedEnvVars,
          selectedDesignSystem,
        );

      track("ADD_PROJECT", { step: "GENERATE", canvas_id: createCanvas.id });
      track("AI_PROMPT_REQUESTED", {
        type: "AI_NEW_PROJECT",
        prompt: generateTextInput,
        imageUrls: uploadedFiles.map((file) => file.url),
        canvas_id: createCanvas.id,
      });

      track("TRACK_USED_RUNNING_TEMPLATE", {
        used: Boolean(createdProject?.templateUserId),
        repoPath: createdProject?.repoPath,
      });

      // Update store
      dispatch(
        onProjectsSnapshot({
          newItems: [createdProject],
          deletedItems: [],
        }),
      );

      if (
        selectedTemplateStack?.includes("SUPABASE") &&
        supabaseConnectedProjectId
      ) {
        try {
          // Make the API call after the project is created now that we can
          await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/projects/supabase-connect/${createdProject.id}/connect-project`,
            {
              supabaseProjectId: supabaseConnectedProjectId,
              isFtue: true,

              // Also set the env variables in supabase
              envVariablesForSupabase: formattedEnvVars,
            },
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            },
          );
        } catch (e) {
          logAxiosError("Error connecting supabase project", e);
          toast.error(
            `Error connecting Supabase project, continuing without it: ${getAxiosErorrMessage(
              e,
            )}`,
          );
        }
      }

      // Upload any pending codebase files after canvas creation and dev server is ready
      if (pendingCodebaseFiles.length > 0) {
        // Don't await this - let it run in the background while user navigates
        uploadPendingFilesWhenReady(
          createCanvas.id,
          pendingCodebaseFiles,
          uploadFiles,
          supabaseJWT,
        )
          .then(() => {
            clearPendingCodebaseFiles();
            toast.success(
              `Uploaded ${pendingCodebaseFiles.length} file(s) to codebase`,
            );
          })
          .catch((error) => {
            console.error("Error uploading pending codebase files:", error);
            toast.error("Some files could not be uploaded to codebase");
          });
      }
      navigate(`/canvases/${createCanvas.id}/editor?generate-prd=t`);
    } catch (error) {
      logAxiosError("Error creating new project", error);

      // Track project creation failure
      track("PROJECT_CREATION_STEP", {
        step: "PROJECT_CREATION_FAILED",
        canvas_id: null,
        error: getAxiosErorrMessage(error),
        template_stack: selectedTemplateStack,
        framework: reactFramework,
      });

      toast.error(
        `Could not create a new project: ${getAxiosErorrMessage(error)}`,
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      setGenerateAttachedFiles(
        uploadedFiles.map((file) => ({
          url: file.url,
          name: file.name,
          type: file.type,
          mimeType: file.mimeType,
        })),
      );
    } else {
      setGenerateAttachedFiles(null);
    }
  }, [uploadedFiles, setGenerateAttachedFiles]);

  // const optionsConfig = useMemo(() => {
  //   let items = [];

  // }, []);

  const templateStackKind =
    selectedTemplateStack === TemplateStack.BLANK
      ? "none"
      : CustomDesignSystems.find(
            (designSystem) =>
              designSystem.templateStack === selectedTemplateStack,
          )
        ? "design-system"
        : "database+payments";

  const dbItems = useMemo(() => {
    return (
      [
        {
          value: "supabase",
          label: "Supabase",
          icon: "/images/supabase-logo-icon.svg",
          stacks: [
            TemplateStack.SUPABASE,
            TemplateStack.SUPABASE_POLAR,
            TemplateStack.SUPABASE_STRIPE,
          ],
        },
        {
          value: "clerk-convex",
          label: "Clerk + Convex",
          icon: "/images/clerk-convex.svg",
          stacks: [
            TemplateStack.CLERK_CONVEX,
            TemplateStack.CLERK_CONVEX_POLAR,
            TemplateStack.CLERK_CONVEX_STRIPE,
          ],
        },
      ] as const
    )
      .map((option) =>
        option.stacks.some((stack) =>
          isValidConfiguration({
            reactFramework,
            selectedTemplateStack: stack,
          }),
        )
          ? option
          : null,
      )
      .filter((x) => x !== null);
  }, [reactFramework]);

  const selectedDbItem = useMemo(() => {
    return (
      dbItems.find((item) =>
        item.stacks.some((stack) => stack === selectedTemplateStack),
      )?.value ?? ""
    );
  }, [dbItems, selectedTemplateStack]);

  const paymentItems = useMemo(() => {
    return (
      [
        {
          value: "stripe",
          label: "Stripe",
          icon: "/images/stripe-icon.svg",
          stacks: [
            TemplateStack.SUPABASE_STRIPE,
            TemplateStack.CLERK_CONVEX_STRIPE,
          ],
        },
        {
          value: "polar",
          label: "Polar",
          icon: "/images/polar-sh_dark.svg",
          stacks: [
            TemplateStack.SUPABASE_POLAR,
            TemplateStack.CLERK_CONVEX_POLAR,
          ],
        },
      ] as const
    )
      .map((option) =>
        option.stacks.some((stack) =>
          isValidConfiguration({
            reactFramework,
            selectedTemplateStack: stack,
          }),
        )
          ? option
          : null,
      )
      .filter((x) => x !== null);
  }, [reactFramework]);

  const selectedPaymentItem = useMemo(() => {
    return (
      paymentItems.find((item) =>
        item.stacks.some((stack) => stack === selectedTemplateStack),
      )?.value ?? ""
    );
  }, [paymentItems, selectedTemplateStack]);

  const setDbAndPaymentItem = (
    db: string | undefined,
    payment: string | undefined,
    priorityField: "db" | "payment",
  ) => {
    const dbStacks = dbItems.find((item) => item.value === db)?.stacks;
    const paymentStacks = paymentItems.find(
      (item) => item.value === payment,
    )?.stacks;
    if (!dbStacks) {
      setSelectedTemplateStack(
        priorityField === "db"
          ? TemplateStack.BLANK
          : (paymentStacks?.[0] ?? TemplateStack.BLANK),
      );
      return;
    }
    if (!paymentStacks) {
      setSelectedTemplateStack(dbStacks[0]);
      return;
    }
    const commonStacks = paymentStacks.filter((stack) =>
      dbStacks.some((s) => s === stack),
    );
    if (commonStacks.length === 0) {
      setSelectedTemplateStack(TemplateStack.BLANK);
      return;
    }
    setSelectedTemplateStack(commonStacks[0]);
  };

  const designSystemItems = useMemo(() => {
    return CustomDesignSystems.filter((designSystem) => {
      return activeFeatureFlags?.includes(designSystem.featureFlag);
    }).map((designSystem) => ({
      value: designSystem.importName,
      label: designSystem.title,
      ...designSystem,
    }));
  }, [activeFeatureFlags]);

  const frameworkCategory = useMemo(() => {
    if (
      reactFramework === ReactFramework.VITE ||
      reactFramework === ReactFramework.VITE_REACT_ROUTER_V7 ||
      reactFramework === ReactFramework.VITE_WITH_VIKE
    ) {
      return ReactFramework.VITE;
    }
    if (
      reactFramework === ReactFramework.NEXT_JS ||
      reactFramework === ReactFramework.NEXT_JS_APP_ROUTER ||
      reactFramework === ReactFramework.NEXT_JS_TURBOPACK
    ) {
      return ReactFramework.NEXT_JS;
    }
    return reactFramework;
  }, [reactFramework]);

  const designSystemCategory = useMemo(() => {
    if (
      CustomDesignSystems.some(
        (system) => system.templateStack === selectedTemplateStack,
      )
    ) {
      return selectedTemplateStack;
    }
    return TemplateStack.BLANK;
  }, [selectedTemplateStack]);

  const designSystemDisabledReason =
    templateStackKind !== "design-system" && templateStackKind !== "none"
      ? "Custom design systems are not compatible with auth/db/payments"
      : undefined;

  const frameworkDisabledReason =
    templateStackKind !== "database+payments" && templateStackKind !== "none"
      ? "Custom design systems are not compatible with auth/db/payments"
      : reactFramework === ReactFramework.VITE_REACT_ROUTER_V7
        ? "React Router 7 is not compatible with auth/db/payments"
        : reactFramework === ReactFramework.NEXT_JS_TURBOPACK
          ? "Next.js Turbopack is not compatible with auth/db/payments"
          : undefined;

  const isGithubConnected = useIsGithubConnected();
  const isGitlabConnected = useIsGitlabConnected();
  const isBitbucketConnected = useIsBitbucketConnected();

  const nothingConnected =
    !isGithubConnected && !isGitlabConnected && !isBitbucketConnected;

  return (
    <>
      {showIntegrationSetup && (
        <Dialog open onOpenChange={() => setShowIntegrationSetup(undefined)}>
          <DialogContent className="max-h-screen overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>Setup integrations</DialogTitle>
              <DialogDescription></DialogDescription>
            </DialogHeader>
            <ProviderSetup
              saveActionText={
                showIntegrationSetup === "configureAndGenerate"
                  ? "Save and Generate"
                  : "Save"
              }
              templateStack={selectedTemplateStack}
              reactFramework={reactFramework}
              onBack={() => {
                setShowIntegrationSetup(undefined);
              }}
              onComplete={() => {
                setTemplateStackConfigured(true);
                setShowIntegrationSetup(undefined);
                if (showIntegrationSetup === "configureAndGenerate") {
                  generate(true);
                }
              }}
              formValues={envVariables}
              onFormValuesChange={(formValues) => {
                setEnvVariables(formValues);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      <StylesBuilderModal onApply={handleStylesBuilderApply} />
      <form
        className="flex w-fit gap-3 flex-col text-md mx-auto px-8"
        onSubmit={() => loading || generate()}
      >
        <div className="font-medium text-[36px] font-brockmann leading-[57.6px] text-center self-start w-full">
          What do you want to build?
        </div>
        <div
          className="[background:padding-box_linear-gradient(var(--secondary-n-950)_0_0),border-box_linear-gradient(130deg,var(--primary-100)_-14.01%,#C1B6F9_-2.4%,#2F3035_12.96%)] rounded-xl p-4 text-xs grid *:row-start-1 *:col-start-1 self-start w-full border border-transparent"
          onPaste={handleDirectPaste}
        >
          <div className="flex flex-col gap-4">
            <div className="flex empty:hidden">
              {uploadedFiles.length !== 0 && <FilePreviewerComponent />}
              {pendingCodebaseFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 my-2">
                  {pendingCodebaseFiles.map((file, index) => (
                    <div
                      key={file.name + file.size + file.lastModified}
                      className="relative w-fit rounded-md"
                    >
                      <X
                        className="absolute cursor-pointer -top-1 -right-1.5 z-10 w-5 h-5 hover:text-icons-primary text-icons-secondary bg-background-secondary hover:bg-background-accent rounded-md p-0.5 size-5"
                        onClick={() => {
                          const newFiles = pendingCodebaseFiles.filter(
                            (_, i) => i !== index,
                          );
                          setPendingCodebaseFiles(newFiles);
                        }}
                      />
                      <div
                        className={cn(
                          "h-16 w-16 rounded-md relative flex items-center justify-center p-1",
                          file.type.startsWith("image/")
                            ? "checkerboard"
                            : "bg-background-secondary",
                        )}
                        style={
                          file.type.startsWith("image/")
                            ? {
                                backgroundImage: `
                        linear-gradient(45deg, var(--background-secondary) 25%, transparent 25%), 
                        linear-gradient(-45deg, var(--background-secondary) 25%, transparent 25%), 
                        linear-gradient(45deg, transparent 75%, var(--background-secondary) 75%), 
                        linear-gradient(-45deg, transparent 75%, var(--background-secondary) 75%)
                      `,
                                backgroundSize: "8px 8px",
                                backgroundPosition:
                                  "0 0, 0 4px, 4px -4px, -4px 0px",
                                backgroundColor: "bg-background-secondary",
                              }
                            : undefined
                        }
                      >
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Pending upload"
                            height={100}
                            width={100}
                            className="h-10 w-16 rounded-sm relative flex items-center justify-center object-contain"
                            onLoad={(e) => {
                              // Clean up the object URL after the image loads
                              setTimeout(
                                () => URL.revokeObjectURL(e.currentTarget.src),
                                1000,
                              );
                            }}
                          />
                        ) : file.type === "application/pdf" ? (
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <FileIcon className="size-4 text-icons-primary" />
                            <span className="text-xs truncate max-w-full">
                              PDF
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center w-full h-full">
                            <FileIcon className="size-4 text-icons-primary" />
                            <span className="text-xs truncate max-w-full">
                              File
                            </span>
                          </div>
                        )}
                        <CloudUpload className="text-icons-secondary absolute bottom-1 left-1 z-10 size-4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Textarea
              className={cn(
                "bg-transparent border-0 outline-0",
                "focus:outline-hidden focus:ring-0 focus:ring-offset-0 focus-visible:ring-0",
                "focus-visible:ring-offset-0 focus:border-0 focus-visible:border-0",
                "text-text-secondary text-base leading-tight",
                "resize-none shadow-none",
                "box-content w-[stretch] p-0",
                "placeholder:text-text-muted text-sm min-h-[2lh]",
              )}
              placeholder="Onboarding page for ed tech app in minimal UI"
              value={generateTextInput || ""}
              rows={1}
              autoResize
              onChange={(e) => setGenerateTextInput(e.target.value)}
              data-testid="generate-prompt-textarea"
            />
            <div className="flex flex-row gap-2">
              <FileUploadComponent
                button={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    type="button"
                    className="text-text-muted"
                    aria-label="Upload File"
                  >
                    <PaperclipIcon className="size-4" role="presentation" />
                  </Button>
                }
              />
              <Button
                variant="ghost"
                size="icon-sm"
                type="button"
                onClick={() => {
                  track("FIGMA_LINK");
                  window.open(
                    "https://www.figma.com/community/plugin/1463689183126672406/tempo-labs",
                    "_blank",
                  );
                }}
                className="text-text-muted"
                data-tooltip-id="tooltip"
                data-tooltip-content="Figma Extension"
                data-tooltip-place="top"
              >
                <img
                  src="/images/figma-logo.svg"
                  alt="Figma"
                  className="h-4 w-4 opacity-60"
                />
              </Button>
              <div
                className={cn(
                  "group ml-auto flex items-center rounded-md transition-colors",
                  hasAppliedStyles
                    ? "bg-primary-n-900/30 hover:bg-primary-n-900/50 pr-1.5"
                    : "hover:bg-secondary-n-900",
                )}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => {
                    track("STYLES_BUILDER_OPEN");
                    openStylesBuilder();
                  }}
                  className={cn(
                    "gap-1.5 hover:bg-transparent hover:text-inherit",
                    hasAppliedStyles
                      ? "text-primary-n-400 pr-0 group-hover:text-primary-n-300"
                      : "text-text-muted group-hover:text-text-primary",
                  )}
                >
                  <Sparkles className="size-4" />
                  <span className="text-xs">Style Builder</span>
                </Button>
                {hasAppliedStyles && (
                  <button
                    type="button"
                    onClick={handleClearStyles}
                    className="ml-0.5 p-1 text-primary-n-400 group-hover:text-primary-n-300 transition-colors"
                    title="Clear styles"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
              <AiModelDropdown
                codeGenerationModel={codeGenerationModel}
                className="hover:text-secondary-n-50 hover:bg-secondary-n-900"
              />
              <Button
                variant="primary"
                size="sm"
                className="rounded-md"
                onClick={() => generate()}
                type="button"
                disabled={loading}
                data-testid="generate-button"
              >
                <div
                  className={cn("animate-spin mr-1", !generating && "hidden")}
                >
                  <Loader2 className="h-4 w-4" />
                </div>
                <span>Generate</span>
              </Button>
            </div>
          </div>
          {isDragging && (
            <DragHoverTargets
              className="h-full z-10"
              pastedFiles={[]} // No longer handle pasted files here since they upload immediately
              isContextDisabled={false}
              onUploadFiles={(files) => {
                uploadFiles(files);
                resetDragState();
              }}
              onUploadToCodebase={(files) => {
                // Store dropped files for later upload since we don't have a canvas ID yet
                addPendingCodebaseFiles(files);
                resetDragState();
                toast.success(
                  `${files.length} file(s) will be uploaded to codebase after project creation`,
                );
              }}
            />
          )}
        </div>

        <div className="flex flex-wrap gap-2 items-stretch justify-center w-fit">
          <ConfigBlock title="Choose framework">
            <ToggleGroup
              type="single"
              value={frameworkCategory}
              onValueChange={(value) =>
                setReactFramework(ReactFramework[value])
              }
              orientation="horizontal"
              className="flex gap-3"
            >
              <TechSubgroup title="Web">
                <TechItem
                  value={ReactFramework.VITE}
                  label="Vite"
                  src="/images/vitejs.svg"
                />
                <TechItem
                  value={ReactFramework.NEXT_JS}
                  label="Next.js"
                  src="/images/nextjs_icon.svg"
                />
              </TechSubgroup>
              <TechSubgroup title="Mobile">
                <TechItem
                  value={ReactFramework.EXPO}
                  label="Expo"
                  src="/images/expo.svg"
                />
              </TechSubgroup>
            </ToggleGroup>
          </ConfigBlock>
          {showDesignSystem && designSystemItems.length > 0 && (
            <ConfigBlock title="Design System">
              <ToggleGroup
                type="single"
                value={designSystemCategory}
                onValueChange={(value) =>
                  setSelectedTemplateStack(TemplateStack[value])
                }
                orientation="horizontal"
                className="flex gap-3"
              >
                <TechSubgroup>
                  <TechItem
                    value={TemplateStack.BLANK}
                    label="No Design System"
                    src={<Ban />}
                  />
                  {designSystemItems.map((designSystem) => (
                    <TechItem
                      disabledReason={
                        designSystemDisabledReason ??
                        (designSystem.framework !== reactFramework
                          ? "This design system is not compatible with the selected framework"
                          : undefined)
                      }
                      key={designSystem.templateStack}
                      value={designSystem.templateStack}
                      label={designSystem.title}
                      src={designSystem.icon}
                    />
                  ))}
                </TechSubgroup>
              </ToggleGroup>
            </ConfigBlock>
          )}
          <ConfigBlock title="Add auth, db and payments">
            <TechPopover
              label={
                <>
                  <Blocks className="w-4 aspect-square" /> Integrations{" "}
                  <CaretDownIcon className="ml-auto w-5 aspect-square opacity-50" />
                </>
              }
              disabledReason={frameworkDisabledReason}
            >
              {dbItems.length > 0 || paymentItems.length > 0 ? (
                <>
                  <TechPopoverGroup
                    label="Authentication & Database"
                    value={selectedDbItem}
                    onValueChange={(value) => {
                      return setDbAndPaymentItem(
                        value,
                        selectedPaymentItem,
                        "db",
                      );
                    }}
                  >
                    {dbItems.map((item) => (
                      <TechPopoverItem key={item.value} value={item.value}>
                        <img
                          src={item.icon}
                          role="presentation"
                          className="w-4 h-4"
                        />
                        {item.label}
                      </TechPopoverItem>
                    ))}
                  </TechPopoverGroup>
                  <div className="-mx-2 my-1 border-b border-b-muted" />
                  <TechPopoverGroup
                    label="Payments"
                    value={selectedPaymentItem}
                    onValueChange={(value) =>
                      setDbAndPaymentItem(selectedDbItem, value, "payment")
                    }
                  >
                    {paymentItems.map((item) => (
                      <TechPopoverItem key={item.value} value={item.value}>
                        <img
                          src={item.icon}
                          role="presentation"
                          className="w-4 h-4"
                        />
                        {item.label}
                      </TechPopoverItem>
                    ))}
                  </TechPopoverGroup>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => setShowIntegrationSetup("configureOnly")}
                  >
                    Set up now
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-2xs text-text-secondary text-wrap text-center flex flex-col items-center py-2 gap-2">
                    {reactFramework === ReactFramework.EXPO ? (
                      <>
                        <Ban className="w-4 h-4" />
                        No integrations for
                        <br />
                        mobile apps yet.
                      </>
                    ) : (
                      "No integrations found"
                    )}
                  </div>
                </>
              )}
            </TechPopover>
          </ConfigBlock>
          <ConfigBlock title="Bring your codebase">
            {isGithubConnected && (
              <Button
                variant="secondary"
                size="sm"
                className="gap-1 whitespace-nowrap h-8"
                type="button"
                asChild
              >
                <Link to="/project-create/existing-project">
                  <img
                    src="/images/github-icon.svg"
                    alt="GitHub"
                    className="w-4 h-4"
                  />{" "}
                  Import from GitHub
                </Link>
              </Button>
            )}
            {isGitlabConnected && (
              <Button
                variant="secondary"
                size="sm"
                className="gap-1 whitespace-nowrap h-8"
                type="button"
                asChild
              >
                <Link to="/project-create/existing-project?provider=gitlab">
                  <img
                    src="/icons/gitlab-logo.svg"
                    alt="GitLab"
                    className="w-4 h-4"
                  />{" "}
                  Import from GitLab
                </Link>
              </Button>
            )}
            {isBitbucketConnected && (
              <Button
                variant="secondary"
                size="sm"
                className="gap-1 whitespace-nowrap h-8"
                type="button"
                asChild
              >
                <Link to="/project-create/existing-project?provider=bitbucket">
                  <img
                    src="/icons/bitbucket-logo.svg"
                    alt="Bitbucket"
                    className="w-4 h-4"
                  />{" "}
                  Import from Bitbucket
                </Link>
              </Button>
            )}
            {nothingConnected && (
              <div
                data-tooltip-id="tooltip"
                data-tooltip-html="Connect a git provider in User Settings from your profile dropdown"
              >
                <Button
                  variant="secondary"
                  size="sm"
                  className="gap-1 whitespace-nowrap h-8"
                  type="button"
                  disabled
                >
                  Import from Git
                </Button>
              </div>
            )}
          </ConfigBlock>
          <ConfigBlock title="Start from scratch">
            <Button
              variant="secondary"
              size="sm"
              className="gap-1 whitespace-nowrap h-8"
              onClick={generateBlankProject}
              type="button"
              disabled={loading}
            >
              <div
                className={cn(
                  "animate-spin mr-1",
                  !creatingBlankProject && "hidden",
                )}
              >
                <Loader2 className="h-4 w-4" />
              </div>
              <Plus
                className={cn("w-3 h-3", creatingBlankProject && "hidden")}
              />{" "}
              Blank Canvas
            </Button>
          </ConfigBlock>
          {frameworkCategory === ReactFramework.VITE &&
            allowPickAlternateFramework && (
              <ConfigBlock title="React Router 7">
                <div className="flex items-center">
                  <div className="text-2xs text-text-muted text-wrap">
                    Use the latest version of React Router with file-based
                    routing
                  </div>
                  <Switch
                    checked={
                      reactFramework === ReactFramework.VITE_REACT_ROUTER_V7
                    }
                    onCheckedChange={(checked) =>
                      setReactFramework(
                        checked
                          ? ReactFramework.VITE_REACT_ROUTER_V7
                          : ReactFramework.VITE,
                      )
                    }
                  />
                </div>
              </ConfigBlock>
            )}
          {frameworkCategory === ReactFramework.NEXT_JS &&
            allowPickAlternateFramework && (
              <ConfigBlock title="Next.js Turbopack">
                <div className="flex items-center">
                  <div className="text-2xs text-text-muted text-wrap">
                    Use the latest version of Next.js with Turbopack
                  </div>
                  <Switch
                    checked={
                      reactFramework === ReactFramework.NEXT_JS_TURBOPACK
                    }
                    onCheckedChange={(checked) =>
                      setReactFramework(
                        checked
                          ? ReactFramework.NEXT_JS_TURBOPACK
                          : ReactFramework.NEXT_JS,
                      )
                    }
                  />
                </div>
              </ConfigBlock>
            )}
        </div>
      </form>
    </>
  );
};

export default NewProject;
